/**
 * Copyright © 2026 Adnuntius AS.
 */
import {SignalResourceListBindingService} from '../common/controller/signal-resource-list-binding.service';
import {DestroyRef, inject, Component, DoCheck, Inject, Input, OnChanges, OnDestroy, SimpleChanges, signal} from '@angular/core';
import {FormsModule, NgForm} from '@angular/forms';
import {UIRouterModule} from '@uirouter/angular';
import * as _ from 'lodash';
import template from './earnings-account-analysis-page.component.html';
import {deferSignalStateWrite} from '../common/util/template-state';
import {SiteBulkActionService} from '../site/site-bulk-action.service';
import {AngularModalService} from '../common/modal/angular-modal.service';
import {SiteAccessModalComponent} from './site-access-modal.component';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import {StateService} from '@uirouter/core';
import {getAxPublisherTeamId} from '../site/site-commons';
import {SignalAdminResourcePageState} from '../common/controller/signal-admin-resource-page-state';
import {firstValueFrom} from 'rxjs';
import {SiteApiService} from '../site/site-api.service';
import {AppSharedModule} from '../common/app-shared.module';
import {AdminListModule} from '../common/directives/admin-list.module';
import {AdnCountrySelectComponent} from '../common/directives/country-select/adn-country-select.component';
import {NotesComponent} from '../common/directives/notes/notes.component';
import {ReportWidgetsModule} from '../report/report-widgets.module';
import {ChartsModule} from '../common/directives/charts.module';
import {StatsTablesModule} from '../common/controller/stats-tables.module';
import {AdStatusLabelComponent} from '../ad-unit/ad-status-component';
import {SearchSelectComponent} from '../common/controller/search-select.component';

type AnalysisTab = 'sites' | 'charts' | 'reports';

@Component({
  standalone: true,
  imports: [
    AppSharedModule,
    FormsModule,
    UIRouterModule,
    AdminListModule,
    AdnCountrySelectComponent,
    NotesComponent,
    ReportWidgetsModule,
    ChartsModule,
    StatsTablesModule,
    AdStatusLabelComponent,
    SearchSelectComponent
  ],
  selector: 'adn-earnings-account-analysis-page',
  template
})
export class EarningsAccountAnalysisPageComponent implements DoCheck, OnChanges, OnDestroy {
  private readonly listLifetime = inject(DestroyRef);
  @Input() model: any;
  @Input() state: SignalAdminResourcePageState<any>;
  @Input() isNew: boolean;

  effectiveIsNew = false;
  notesSaveTarget: any;
  activeTab: AnalysisTab = 'sites';

  sites: any = {};
  get displayItems(): any[] { return this.sites?.listState?.items() || []; }
  get hasItems(): boolean { return this.displayItems.length > 0; }
  site: any;
  private readonly showSiteFormState = signal(false);
  private readonly teamReportState = signal<any>(null);

  get showSiteForm(): boolean { return this.showSiteFormState(); }
  set showSiteForm(value: boolean) { this.showSiteFormState.set(value); }
  get teamReport(): any { return this.teamReportState(); }
  set teamReport(value: any) { this.teamReportState.set(value); }
  copySite: any = {site: null};
  showAdvAccess = false;
  showNotes = false;
  adminUserName = '';
  canSeeReports = false;
  visibleReports = false;

  bulkAction: () => void;

  private lastLoadedModelId: string | undefined;
  private destroyed = false;
  private sitesInitialized = false;

  constructor(
    private readonly $state: StateService,
    @Inject(LocalUserPermissions) LocalUserPermissions: any,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    @Inject(LocalUserProfile) localUserProfile: any,
    private readonly listBinding: SignalResourceListBindingService,
    private readonly siteApi: SiteApiService,
    private readonly siteBulkActionService: SiteBulkActionService,
    private readonly angularModalService: AngularModalService
  ) {
    const username = localUserProfile.get('username');
    if (_.includes(['adnuntius@adnuntius.com', 'broker1@adnuntius.com'], username)) {
      this.showNotes = true;
      this.adminUserName = username;
    }
    this.canSeeReports = LocalUserPermissions.getAnyVisibility().RUN_REPORTS === true;
    this.bulkAction = () => undefined;
  }

  get showNewMessage(): boolean {
    return !!this.effectiveIsNew;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isNew) {
      this.effectiveIsNew = !!this.isNew;
    }
    if (changes.model) {
      this.notesSaveTarget = this.state ? this.state.notesSaveTarget() : this.model;
      this.updateAdvAccess();
    }
    this.refreshSitesIfNeeded();
  }

  ngDoCheck(): void {
    const modelId = this.model?.id;
    if (modelId && modelId !== this.lastLoadedModelId && !this.effectiveIsNew) {
      this.refreshSitesIfNeeded();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
  }

  selectTab(tab: AnalysisTab): void {
    this.activeTab = tab;
    if (tab === 'reports') {
      this.visibleReports = true;
    }
    if (tab === 'sites' && this.showAdvAccess && !this.teamReport) {
      this.loadSiteGroupsQuery();
    }
  }

  loadSiteForm(): void {
    const theCountry = _.isArray(this.model?.countries) ? this.model.countries[0] : null;
    this.site = this.siteApi.create({
      earningsAccount: this.model
    });
    if (theCountry) {
      this.site.country = theCountry;
    }
    const ownerTeamId = getAxPublisherTeamId(this.model);
    if (ownerTeamId) {
      this.site.ownerTeam = {id: ownerTeamId};
    }
    this.showSiteForm = true;
  }

  loadExternalSite(): void {
    const theCountry = _.isArray(this.model?.countries) ? this.model.countries[0] : null;
    this.$state.go('app.inv.sites.new', {
      earningsAccountName: this.model?.name,
      earningsAccountId: _.get(this.model, ['id']),
      earningsAccountCountry: theCountry,
      earningsAccountOwnerTeamId: getAxPublisherTeamId(this.model)
    });
  }

  onSiteBeforeSubmit(): Promise<any> | void {
    if (!_.get(this.copySite, 'site') || _.isArray(this.copySite.site?.teams)) {
      return;
    }
    return firstValueFrom(this.siteApi.get(this.copySite.site.id)).then((theSite) => {
      this.site.teams = theSite.teams;
    });
  }

  saveSite(editForm: NgForm): void {
    if (editForm.invalid || this.sites.listState?.loading()) {
      return;
    }
    const beforeSubmit = this.onSiteBeforeSubmit();
    const savePromise = beforeSubmit
      ? beforeSubmit.then(() => firstValueFrom(this.siteApi.save(this.site)))
      : firstValueFrom(this.siteApi.save(this.site));
    savePromise.then((saved: any) => {
      this.showSiteForm = false;
      if (!_.isArray(this.sites.items)) {
        this.sites.items = [];
      }
      this.sites.addItemToList?.(saved);
    });
  }

  editAccess(teamReport: any): void {
    const modalRef = this.angularModalService.show(SiteAccessModalComponent, {
      class: 'modal-lg',
      initialState: {teamReport}
    });
    (modalRef.content as SiteAccessModalComponent).onSaved.subscribe(() => {
      this.loadSiteGroupsQuery();
    });
  }

  expandSiteLimit(team: any): void {
    team.siteLimit = team.sites.length;
  }

  expandTeamLimit(team: any): void {
    team.teamLimit = team.teams.length;
  }

  expandAdditionalTeamLimit(team: any): void {
    team.additionalTeamLimit = team.additionalTeams.length;
  }

  expandRemovedTeamLimit(team: any): void {
    team.removedTeamLimit = team.removedTeams.length;
  }

  private updateAdvAccess(): void {
    this.showAdvAccess = this.LocalNetworkProfile.isPureMarketplacePlatform()
      && !this.effectiveIsNew
      && this.model?.filterTeamsType === 'NONE';
    if (this.showAdvAccess && this.activeTab === 'sites') {
      this.loadSiteGroupsQuery();
    }
  }

  private loadSiteGroupsQuery(): void {
    if (!this.model?.id) {
      return;
    }
    this.teamReport = false;
    this.siteApi.listOptions({groupByTeams: 'true', earningsAccount: this.model.id}).subscribe((page) => {
      // false → [] flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
      deferSignalStateWrite(() => {
        this.teamReport = page.results;
        _.forEach(this.teamReport, (team: any) => {
          team.teamLimit = 10;
          team.siteLimit = 10;
          team.additionalTeamLimit = 10;
          team.removedTeamLimit = 10;
        });
      });
    });
  }

  private refreshSitesIfNeeded(force = false): void {
    const modelId = this.effectiveIsNew ? undefined : this.model?.id;
    if (!force && modelId === this.lastLoadedModelId) {
      return;
    }
    this.lastLoadedModelId = modelId;
    this.initSitesList();
  }

  private initSitesList(): void {
    if (!this.model?.id || this.effectiveIsNew) {
      this.sites?.disposeList?.();
      this.sites = {};
      this.sitesInitialized = false;

      return;
    }
    if (this.sitesInitialized && this.sites?.moreParams?.earningsAccount === this.model.id) {
      return;
    }
    this.sitesInitialized = true;
    this.sites?.disposeList?.();
    this.sites = {};

    this.sites = this.listBinding.create(this.siteApi, 'fullSitesForEarningsAccount', this.listLifetime, {
      baseTable: 'site',
      moreParams: {earningsAccount: this.model.id},

      afterFill: () => {
        this.copySite.site = this.displayItems[0];
        this.bulkAction = this.siteBulkActionService.createBulkAction(this.sites);
      }
    });
  }
}
